<div class="rfi-wrap">

  <!-- Header -->
  <div class="rfi-header-row">
    <h3 class="rfi-section-title">RFIs — Requests for Information</h3>
    <button mat-raised-button color="primary" (click)="openAdd()" *ngIf="!showForm">
      <mat-icon>add</mat-icon> New RFI
    </button>
  </div>

  <!-- Summary chips -->
  <div class="rfi-summary" *ngIf="rfis.length > 0 && !showForm">
    <div class="rfi-chip rfi-chip-open">
      <span class="rfi-chip-num">{{ openCount }}</span>
      <span class="rfi-chip-label">Open</span>
    </div>
    <div class="rfi-chip rfi-chip-answered">
      <span class="rfi-chip-num">{{ answeredCount }}</span>
      <span class="rfi-chip-label">Answered</span>
    </div>
    <div class="rfi-chip rfi-chip-cost" *ngIf="totalCostImpact > 0">
      <span class="rfi-chip-num">{{ totalCostImpact | currency }}</span>
      <span class="rfi-chip-label">Cost Impact</span>
    </div>
    <div class="rfi-chip rfi-chip-schedule" *ngIf="totalScheduleImpact > 0">
      <span class="rfi-chip-num">{{ totalScheduleImpact }} days</span>
      <span class="rfi-chip-label">Schedule Impact</span>
    </div>
  </div>

  <!-- Form -->
  <div class="rfi-form-card" *ngIf="showForm" [formGroup]="form">
    <h4 class="rfi-form-title">{{ editingRfi ? 'Edit ' + editingRfi.rfiNumber : 'New RFI' }}</h4>

    <div class="rfi-form-grid">
      <mat-form-field appearance="outline">
        <mat-label>RFI Number</mat-label>
        <input matInput formControlName="rfiNumber" placeholder="RFI-001">
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Status</mat-label>
        <mat-select formControlName="status">
          <mat-option *ngFor="let s of statusOptions" [value]="s">{{ s }}</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Submitted By</mat-label>
        <input matInput formControlName="submittedBy" placeholder="Your name / company">
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Submitted To</mat-label>
        <input matInput formControlName="submittedTo" placeholder="Engineer / Architect">
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Date Submitted</mat-label>
        <input matInput type="date" formControlName="dateSubmitted">
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Response Required By</mat-label>
        <input matInput type="date" formControlName="dateRequired">
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Ball in Court</mat-label>
        <input matInput formControlName="ballInCourt" placeholder="Who needs to act next">
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Cost Impact ($)</mat-label>
        <input matInput type="number" formControlName="costImpact" min="0">
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Schedule Impact (days)</mat-label>
        <input matInput type="number" formControlName="scheduleImpactDays" min="0">
      </mat-form-field>
    </div>

    <mat-form-field appearance="outline" class="rfi-full">
      <mat-label>Subject</mat-label>
      <input matInput formControlName="subject" placeholder="Brief subject line">
    </mat-form-field>

    <mat-form-field appearance="outline" class="rfi-full">
      <mat-label>Description / Question</mat-label>
      <textarea matInput formControlName="description" rows="4"
        placeholder="Describe the question or clarification needed..."></textarea>
    </mat-form-field>

    <div class="rfi-answer-section" *ngIf="form.get('status')?.value === 'Answered' || form.get('status')?.value === 'Closed'">
      <mat-form-field appearance="outline" class="rfi-full">
        <mat-label>Date Answered</mat-label>
        <input matInput type="date" formControlName="dateAnswered">
      </mat-form-field>
      <mat-form-field appearance="outline" class="rfi-full">
        <mat-label>Answer / Response</mat-label>
        <textarea matInput formControlName="answer" rows="3"
          placeholder="Official response from engineer / architect..."></textarea>
      </mat-form-field>
    </div>

    <div class="rfi-form-actions">
      <button mat-stroked-button (click)="cancel()">Cancel</button>
      <button mat-raised-button color="primary" (click)="submit()">
        <mat-icon>save</mat-icon> {{ editingRfi ? 'Update' : 'Create RFI' }}
      </button>
    </div>
  </div>

  <!-- Loading -->
  <div class="rfi-loading" *ngIf="isLoading">
    <div class="css-spinner"></div>
  </div>

  <!-- Empty state -->
  <div class="rfi-empty" *ngIf="!isLoading && rfis.length === 0 && !showForm">
    <mat-icon>quiz</mat-icon>
    <p>No RFIs yet. Create one to track open questions with engineers or architects.</p>
  </div>

  <!-- Table -->
  <div class="rfi-table-wrap" *ngIf="!isLoading && rfis.length > 0 && !showForm">
    <table mat-table [dataSource]="rfis" class="rfi-table">

      <ng-container matColumnDef="rfiNumber">
        <th mat-header-cell *matHeaderCellDef>RFI #</th>
        <td mat-cell *matCellDef="let r">
          <strong>{{ r.rfiNumber }}</strong>
          <span class="rfi-overdue-tag" *ngIf="isOverdue(r)">OVERDUE</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="subject">
        <th mat-header-cell *matHeaderCellDef>Subject</th>
        <td mat-cell *matCellDef="let r" class="rfi-subject-cell">{{ r.subject }}</td>
      </ng-container>

      <ng-container matColumnDef="submittedTo">
        <th mat-header-cell *matHeaderCellDef>To</th>
        <td mat-cell *matCellDef="let r">{{ r.submittedTo }}</td>
      </ng-container>

      <ng-container matColumnDef="dateRequired">
        <th mat-header-cell *matHeaderCellDef>Due</th>
        <td mat-cell *matCellDef="let r" [class.rfi-overdue]="isOverdue(r)">
          {{ r.dateRequired | date:'M/d/yy' }}
        </td>
      </ng-container>

      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef>Status</th>
        <td mat-cell *matCellDef="let r">
          <span class="rfi-status-badge" [ngClass]="statusClass(r.status)">{{ r.status }}</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="ballInCourt">
        <th mat-header-cell *matHeaderCellDef>Ball in Court</th>
        <td mat-cell *matCellDef="let r">{{ r.ballInCourt || '—' }}</td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let r">
          <button mat-icon-button (click)="openEdit(r)" matTooltip="Edit">
            <mat-icon>edit</mat-icon>
          </button>
          <button mat-icon-button color="warn" (click)="remove(r)" matTooltip="Delete">
            <mat-icon>delete</mat-icon>
          </button>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;"
          [class.rfi-row-open]="row.status === 'Open'"
          [class.rfi-row-overdue]="isOverdue(row)"></tr>
    </table>
  </div>

</div>
